import * as React from 'react'
import * as Reakit from 'reakit/Composite'

import { PopoverStateReturn } from '../popover'
import { SelectItem } from '../select'

export const ListContext = React.createContext<Reakit.CompositeStateReturn | null>(null)

export function useListState(options?: Reakit.CompositeInitialState) {
  return Reakit.useCompositeState({ ...options, loop: true, unstable_virtual: true })
}

function getSelectedItemIndex(items: SelectItem<unknown>[], value: unknown) {
  if (!value && value !== 0) return -1

  return items.findIndex((item) => item.value === value)
}

/**
 * Hook which handles select state and returns onChange handler
 */
export function useList<ValueType, ItemType extends SelectItem<ValueType>>(
  state: PopoverStateReturn,
  defaultValue: ValueType | undefined,
  items: ItemType[],
  onChange: (item: ItemType) => void
) {
  const [selectedIndex, setSelectedIndex] = React.useState(getSelectedItemIndex(items, defaultValue))
  const closePopover = React.useRef(() => state.hide())

  // Update select value on change from outside
  React.useEffect(() => {
    const newIndex = getSelectedItemIndex(items, defaultValue)
    setSelectedIndex(newIndex)
  }, [items, selectedIndex, defaultValue])

  const onItemChange = React.useCallback(
    (index: number, closeOnSelect = true) => {
      const selectedItem = items[index]
      setSelectedIndex(index)

      if (closeOnSelect) closePopover.current()
      onChange(selectedItem)
    },
    [items, onChange, setSelectedIndex]
  )

  return { selectedIndex, onItemChange }
}
